// Position-based Verlet cloth used for sails and flags. Runs in ship-local
// space; the caller supplies effective gravity (gravity minus the ship's
// acceleration) and the apparent wind.

const AIR_DENSITY = 1.225;

export interface Capsule {
  ax: number;
  ay: number;
  az: number;
  bx: number;
  by: number;
  bz: number;
  r: number;
}

/** Values per link record in the solver's list (the ends are kept apart, as integers). */
const LINK = 5;

export class Cloth {
  readonly cols: number;
  readonly rows: number;
  readonly count: number;
  readonly pos: Float32Array;
  readonly prev: Float32Array;
  readonly normal: Float32Array;
  readonly invMass: Float32Array;
  readonly mass: Float32Array;
  /** Grid coordinates (u along columns, v along rows), 0..1. */
  readonly uv: Float32Array;
  readonly indices: Uint32Array;
  private readonly consA: Int32Array;
  private readonly consB: Int32Array;
  private readonly rest: Float32Array;
  private readonly restBase: Float32Array;
  private readonly stiff: Float32Array;
  /** 0 = horizontal, 1 = vertical, 2 = shear/bend. */
  private readonly axis: Uint8Array;
  /**
   * The links that can move at all this step, one record each, laid out in
   * order so the solver reads straight through: the two particles' offsets,
   * the rest length, and the share of a correction each end takes times the
   * stiffness (stretched, then compressed).
   */
  private readonly links: Float64Array;
  private readonly linkEnds: Int32Array;
  colliders: Capsule[] = [];
  /** Mean |normal pressure coefficient| over the last step, 0..1. */
  fill = 0;
  /** How much the cloth is flogging (luffing), 0..1. */
  flutter = 0;
  /** Resultant aerodynamic force on the cloth (ship-local, N). */
  readonly force = new Float64Array(3);
  private readonly accel: Float32Array;
  private readonly edge: Float32Array;
  /** Per-triangle / per-constraint survival after tearing. */
  readonly triAlive: Uint8Array;
  private readonly consAlive: Uint8Array;
  /** Particles torn free of their spar can no longer be pinned. */
  private readonly detached: Uint8Array;
  private readonly triArea0: Float32Array;
  private readonly totalArea: number;
  /** Fraction of the cloth still intact, 0..1. */
  health = 1;
  /** Bumped whenever the topology changes (for renderers). */
  version = 0;

  constructor(cols: number, rows: number, init: (u: number, v: number, out: number[]) => void, massPerArea = 0.9) {
    this.cols = cols;
    this.rows = rows;
    const n = cols * rows;
    this.count = n;
    this.pos = new Float32Array(n * 3);
    this.prev = new Float32Array(n * 3);
    this.normal = new Float32Array(n * 3);
    this.invMass = new Float32Array(n);
    this.mass = new Float32Array(n);
    this.uv = new Float32Array(n * 2);
    this.accel = new Float32Array(n * 3);
    this.edge = new Float32Array(n);
    const p = [0, 0, 0];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        const u = c / (cols - 1);
        const v = r / (rows - 1);
        init(u, v, p);
        this.pos.set(p, i * 3);
        this.prev.set(p, i * 3);
        this.uv[i * 2] = u;
        this.uv[i * 2 + 1] = v;
        const eu = Math.min(u, 1 - u);
        const ev = Math.min(v, 1 - v);
        this.edge[i] = 1 - Math.min(1, Math.min(eu, ev) * 4);
      }
    }
    const tris: number[] = [];
    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const a = r * cols + c;
        const b = a + 1;
        const d = a + cols;
        const e = d + 1;
        tris.push(a, d, b, b, d, e);
      }
    }
    this.indices = new Uint32Array(tris);
    this.triAlive = new Uint8Array(tris.length / 3).fill(1);
    this.detached = new Uint8Array(n);

    const ca: number[] = [];
    const cb: number[] = [];
    const st: number[] = [];
    const ax: number[] = [];
    const add = (i: number, j: number, s: number, k: number) => {
      ca.push(i);
      cb.push(j);
      st.push(s);
      ax.push(k);
    };
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        if (c + 1 < cols) add(i, i + 1, 1, 0);
        if (r + 1 < rows) add(i, i + cols, 1, 1);
        if (c + 1 < cols && r + 1 < rows) {
          add(i, i + cols + 1, 0.5, 2);
          add(i + 1, i + cols, 0.5, 2);
        }
        if (c + 2 < cols) add(i, i + 2, 0.32, 0);
        if (r + 2 < rows) add(i, i + cols * 2, 0.32, 1);
      }
    }
    this.consAlive = new Uint8Array(ca.length).fill(1);
    this.links = new Float64Array(ca.length * LINK);
    this.linkEnds = new Int32Array(ca.length * 2);
    this.consA = new Int32Array(ca);
    this.consB = new Int32Array(cb);
    this.stiff = new Float32Array(st);
    this.axis = new Uint8Array(ax);
    this.rest = new Float32Array(ca.length);
    this.restBase = new Float32Array(ca.length);
    for (let k = 0; k < ca.length; k++) {
      const i = ca[k]! * 3;
      const j = cb[k]! * 3;
      const d = Math.hypot(this.pos[i]! - this.pos[j]!, this.pos[i + 1]! - this.pos[j + 1]!, this.pos[i + 2]! - this.pos[j + 2]!);
      this.rest[k] = d;
      this.restBase[k] = d;
    }
    // Lumped particle masses from triangle areas.
    this.triArea0 = new Float32Array(this.indices.length / 3);
    let total = 0;
    for (let t = 0; t < this.indices.length; t += 3) {
      const a = this.indices[t]!, b = this.indices[t + 1]!, c = this.indices[t + 2]!;
      const area = triArea(this.pos, a, b, c);
      this.triArea0[t / 3] = area;
      total += area;
      for (const i of [a, b, c]) this.mass[i]! += (area * massPerArea) / 3;
    }
    this.totalArea = Math.max(total, 1e-6);
    for (let i = 0; i < n; i++) {
      this.mass[i] = Math.max(this.mass[i]!, 0.05);
      this.invMass[i] = 1 / this.mass[i]!;
    }
    this.computeNormals();
  }

  pin(i: number, x: number, y: number, z: number, teleport = false): void {
    if (this.detached[i]) return;
    this.invMass[i] = 0;
    const o = i * 3;
    if (teleport) {
      this.prev[o] = x;
      this.prev[o + 1] = y;
      this.prev[o + 2] = z;
    } else {
      this.prev[o] = this.pos[o]!;
      this.prev[o + 1] = this.pos[o + 1]!;
      this.prev[o + 2] = this.pos[o + 2]!;
    }
    this.pos[o] = x;
    this.pos[o + 1] = y;
    this.pos[o + 2] = z;
  }

  private restH = NaN;
  private restV = NaN;

  /** Scale rest lengths along rows (vertical) and columns (horizontal). */
  setRestScale(horizontal: number, vertical: number): void {
    // Called every step; the sail's set seldom changes.
    if (horizontal === this.restH && vertical === this.restV) return;
    this.restH = horizontal;
    this.restV = vertical;
    for (let k = 0; k < this.rest.length; k++) {
      const a = this.axis[k]!;
      const s = a === 0 ? horizontal : a === 1 ? vertical : Math.sqrt((horizontal * horizontal + vertical * vertical) / 2);
      this.rest[k] = this.restBase[k]! * s;
    }
  }

  /**
   * Advance one step.
   * @param g effective gravity in cloth space (m/s^2)
   * @param w apparent wind in cloth space (m/s)
   */
  step(dt: number, gx: number, gy: number, gz: number, wx: number, wy: number, wz: number, time: number, turbulence: number, iterations = 8): void {
    const n = this.count;
    const pos = this.pos;
    const prev = this.prev;
    const acc = this.accel;
    acc.fill(0);
    const fr = this.force;
    fr[0] = fr[1] = fr[2] = 0;

    // Aerodynamic pressure per triangle, distributed to its corners.
    let fillSum = 0;
    let areaSum = 0;
    const idx = this.indices;
    const invDt = 1 / dt;
    const wlen = Math.sqrt(wx * wx + wy * wy + wz * wz);
    const alive = this.triAlive;
    for (let t = 0, tri = 0; t < idx.length; t += 3, tri++) {
      if (!alive[tri]) continue;
      const a = idx[t]! * 3, b = idx[t + 1]! * 3, c = idx[t + 2]! * 3;
      const e1x = pos[b]! - pos[a]!, e1y = pos[b + 1]! - pos[a + 1]!, e1z = pos[b + 2]! - pos[a + 2]!;
      const e2x = pos[c]! - pos[a]!, e2y = pos[c + 1]! - pos[a + 1]!, e2z = pos[c + 2]! - pos[a + 2]!;
      let nx = e1y * e2z - e1z * e2y;
      let ny = e1z * e2x - e1x * e2z;
      let nz = e1x * e2y - e1y * e2x;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len < 1e-8) continue;
      const area = len * 0.5;
      nx /= len;
      ny /= len;
      nz /= len;
      const vx = ((pos[a]! - prev[a]!) + (pos[b]! - prev[b]!) + (pos[c]! - prev[c]!)) * invDt / 3;
      const vy = ((pos[a + 1]! - prev[a + 1]!) + (pos[b + 1]! - prev[b + 1]!) + (pos[c + 1]! - prev[c + 1]!)) * invDt / 3;
      const vz = ((pos[a + 2]! - prev[a + 2]!) + (pos[b + 2]! - prev[b + 2]!) + (pos[c + 2]! - prev[c + 2]!)) * invDt / 3;
      const rx = wx - vx, ry = wy - vy, rz = wz - vz;
      const vn = rx * nx + ry * ny + rz * nz;
      const f = 0.5 * AIR_DENSITY * 1.25 * vn * Math.abs(vn) * area;
      // Skin friction along the relative wind.
      const rl = Math.sqrt(rx * rx + ry * ry + rz * rz);
      const fd = 0.5 * AIR_DENSITY * 0.03 * rl * area;
      const fx = nx * f + rx * fd, fy = ny * f + ry * fd, fz = nz * f + rz * fd;
      fr[0]! += fx;
      fr[1]! += fy;
      fr[2]! += fz;
      const fx3 = fx / 3, fy3 = fy / 3, fz3 = fz / 3;
      acc[a]! += fx3; acc[a + 1]! += fy3; acc[a + 2]! += fz3;
      acc[b]! += fx3; acc[b + 1]! += fy3; acc[b + 2]! += fz3;
      acc[c]! += fx3; acc[c + 1]! += fy3; acc[c + 2]! += fz3;
      if (wlen > 0.1) {
        fillSum += (Math.abs(vn) / wlen) * area;
        areaSum += area;
      }
    }
    this.fill = areaSum > 0 ? fillSum / areaSum : 0;

    // Luffing: when the wind runs along the cloth it flogs. Drive a travelling
    // ripple along the chord, strongest at free edges.
    const q = 0.5 * AIR_DENSITY * wlen * wlen;
    const luff = Math.pow(1 - Math.min(1, this.fill * 2.2), 2);
    const flog = q * (0.05 + luff * 0.9 + turbulence * 0.15);
    this.flutter = luff * Math.min(1, wlen / 8);
    const nrm = this.normal;
    const damping = 0.994;
    const dt2 = dt * dt;
    for (let i = 0; i < n; i++) {
      const o = i * 3;
      const im = this.invMass[i]!;
      if (im === 0) continue;
      const u = this.uv[i * 2]!, v = this.uv[i * 2 + 1]!;
      const ph = time * (7 + wlen * 0.6) - u * 9 - v * 3.1;
      const wob = (Math.sin(ph) + 0.5 * Math.sin(ph * 2.3 + 1.7)) * flog * (0.25 + this.edge[i]! * 0.75) * 0.8;
      const ax = gx + (acc[o]! + nrm[o]! * wob) * im;
      const ay = gy + (acc[o + 1]! + nrm[o + 1]! * wob) * im;
      const az = gz + (acc[o + 2]! + nrm[o + 2]! * wob) * im;
      const px = pos[o]!, py = pos[o + 1]!, pz = pos[o + 2]!;
      pos[o] = px + (px - prev[o]!) * damping + ax * dt2;
      pos[o + 1] = py + (py - prev[o + 1]!) * damping + ay * dt2;
      pos[o + 2] = pz + (pz - prev[o + 2]!) * damping + az * dt2;
      prev[o] = px;
      prev[o + 1] = py;
      prev[o + 2] = pz;
    }

    const ca = this.consA, cb = this.consB, rest = this.rest, stiff = this.stiff, im = this.invMass;
    // What each link does doesn't change between iterations: work it out once.
    // Canvas resists stretch strongly but buckles freely in compression; the
    // skip-one (bending) links resist folding more, which keeps the cloth
    // from crumpling into facets.
    const ok = this.consAlive, L = this.links, E = this.linkEnds;
    let end = 0, e = 0;
    for (let k = 0; k < ca.length; k++) {
      if (!ok[k]) continue;
      const wi = im[ca[k]!]!, wj = im[cb[k]!]!;
      const ws = wi + wj;
      if (ws === 0) continue;
      const st = stiff[k]!;
      const sc = st * (st < 0.4 ? 0.55 : 0.08);
      E[e++] = ca[k]! * 3;
      E[e++] = cb[k]! * 3;
      L[end] = rest[k]!;
      L[end + 1] = (st * wi) / ws;
      L[end + 2] = (st * wj) / ws;
      L[end + 3] = (sc * wi) / ws;
      L[end + 4] = (sc * wj) / ws;
      end += LINK;
    }
    for (let it = 0; it < iterations; it++) {
      for (let l = 0, e2 = 0; l < end; l += LINK, e2 += 2) {
        const a = E[e2]!, b = E[e2 + 1]!;
        const dx = pos[b]! - pos[a]!, dy = pos[b + 1]! - pos[a + 1]!, dz = pos[b + 2]! - pos[a + 2]!;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d < 1e-9) continue;
        const diff = (d - L[l]!) / d;
        const si = diff * (diff > 0 ? L[l + 1]! : L[l + 3]!);
        const sj = diff * (diff > 0 ? L[l + 2]! : L[l + 4]!);
        pos[a]! += dx * si;
        pos[a + 1]! += dy * si;
        pos[a + 2]! += dz * si;
        pos[b]! -= dx * sj;
        pos[b + 1]! -= dy * sj;
        pos[b + 2]! -= dz * sj;
      }
      if (this.colliders.length) this.collide();
    }
    this.computeNormals();
  }

  private collide(): void {
    const pos = this.pos;
    for (const c of this.colliders) {
      const abx = c.bx - c.ax, aby = c.by - c.ay, abz = c.bz - c.az;
      const ab2 = abx * abx + aby * aby + abz * abz;
      for (let i = 0; i < this.count; i++) {
        if (this.invMass[i] === 0) continue;
        const o = i * 3;
        const px = pos[o]! - c.ax, py = pos[o + 1]! - c.ay, pz = pos[o + 2]! - c.az;
        const t = Math.max(0, Math.min(1, (px * abx + py * aby + pz * abz) / ab2));
        const dx = px - abx * t, dy = py - aby * t, dz = pz - abz * t;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < c.r * c.r && d2 > 1e-10) {
          const d = Math.sqrt(d2);
          const s = (c.r - d) / d;
          pos[o]! += dx * s;
          pos[o + 1]! += dy * s;
          pos[o + 2]! += dz * s;
        }
      }
    }
  }

  /**
   * Tear a hole of radius r around a point (cloth space). Pinned particles
   * inside it are torn off their spar. Returns the area fraction lost.
   */
  tear(x: number, y: number, z: number, r: number): number {
    const pos = this.pos;
    const r2 = r * r;
    const hit = new Uint8Array(this.count);
    let any = false;
    for (let i = 0; i < this.count; i++) {
      const dx = pos[i * 3]! - x, dy = pos[i * 3 + 1]! - y, dz = pos[i * 3 + 2]! - z;
      if (dx * dx + dy * dy + dz * dz < r2) {
        hit[i] = 1;
        any = true;
      }
    }
    if (!any) {
      // Always take at least the nearest particle so a hit leaves a mark.
      let best = -1, bd = Infinity;
      for (let i = 0; i < this.count; i++) {
        const dx = pos[i * 3]! - x, dy = pos[i * 3 + 1]! - y, dz = pos[i * 3 + 2]! - z;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bd) { bd = d; best = i; }
      }
      if (best < 0 || bd > (r * 3) * (r * 3)) return 0;
      hit[best] = 1;
    }
    return this.removeParticles(hit);
  }

  /**
   * Dev: a new cloth bent on in place of a torn one. Particles that came
   * away are put back among their neighbours for the stitching to pull tight.
   */
  mend(): void {
    const { cols, rows, pos, prev, detached } = this;
    let left = detached.reduce((n, d) => n + d, 0);
    for (let pass = 0; left > 0 && pass < cols + rows; pass++) {
      for (let i = 0; i < this.count; i++) {
        if (!detached[i]) continue;
        const r = Math.floor(i / cols), c = i % cols;
        let x = 0, y = 0, z = 0, n = 0;
        for (const [dr, dc] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const rr = r + dr, cc = c + dc;
          if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) continue;
          const j = rr * cols + cc;
          if (detached[j]) continue;
          x += pos[j * 3]!;
          y += pos[j * 3 + 1]!;
          z += pos[j * 3 + 2]!;
          n++;
        }
        if (n === 0) continue;
        pos[i * 3] = x / n;
        pos[i * 3 + 1] = y / n;
        pos[i * 3 + 2] = z / n;
        detached[i] = 0;
        left--;
      }
    }
    detached.fill(0);
    prev.set(pos);
    this.triAlive.fill(1);
    this.consAlive.fill(1);
    for (let i = 0; i < this.count; i++) this.invMass[i] = 1 / this.mass[i]!;
    this.health = 1;
    this.version++;
  }

  /**
   * The sailmakers at work: up to `n` torn-away particles stitched back in
   * among their whole neighbours (from the edge of a hole inward), and the
   * canvas between them made good. Returns how many were stitched.
   */
  patch(n: number): number {
    const { cols, rows, pos, prev, detached } = this;
    let done = 0;
    for (let i = 0; i < this.count && done < n; i++) {
      if (!detached[i]) continue;
      const r = Math.floor(i / cols), c = i % cols;
      let x = 0, y = 0, z = 0, k = 0;
      for (const [dr, dc] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const rr = r + dr, cc = c + dc;
        if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) continue;
        const j = rr * cols + cc;
        if (detached[j]) continue;
        x += pos[j * 3]!;
        y += pos[j * 3 + 1]!;
        z += pos[j * 3 + 2]!;
        k++;
      }
      if (k === 0) continue;
      pos[i * 3] = prev[i * 3] = x / k;
      pos[i * 3 + 1] = prev[i * 3 + 1] = y / k;
      pos[i * 3 + 2] = prev[i * 3 + 2] = z / k;
      detached[i] = 0;
      this.invMass[i] = 1 / this.mass[i]!;
      done++;
    }
    if (!done) return 0;
    for (let k = 0; k < this.consA.length; k++) {
      if (!this.consAlive[k] && !detached[this.consA[k]!] && !detached[this.consB[k]!]) this.consAlive[k] = 1;
    }
    let area = 0;
    for (let t = 0; t < this.triAlive.length; t++) {
      const a = this.indices[t * 3]!, b = this.indices[t * 3 + 1]!, c = this.indices[t * 3 + 2]!;
      if (!this.triAlive[t] && !detached[a] && !detached[b] && !detached[c]) this.triAlive[t] = 1;
      if (this.triAlive[t]) area += this.triArea0[t]!;
    }
    this.health = area / this.totalArea;
    this.version++;
    return done;
  }

  /** Burn away `fraction` of the remaining cloth at random spots. */
  burn(fraction: number, rand: () => number): number {
    const hit = new Uint8Array(this.count);
    const n = Math.max(1, Math.round(this.count * fraction));
    for (let k = 0; k < n; k++) hit[Math.floor(rand() * this.count)] = 1;
    return this.removeParticles(hit);
  }

  private removeParticles(hit: Uint8Array): number {
    const before = this.health;
    for (let k = 0; k < this.consA.length; k++) {
      if (hit[this.consA[k]!] || hit[this.consB[k]!]) this.consAlive[k] = 0;
    }
    let area = 0;
    for (let t = 0; t < this.triAlive.length; t++) {
      const a = this.indices[t * 3]!, b = this.indices[t * 3 + 1]!, c = this.indices[t * 3 + 2]!;
      if (hit[a] || hit[b] || hit[c]) this.triAlive[t] = 0;
      if (this.triAlive[t]) area += this.triArea0[t]!;
    }
    for (let i = 0; i < this.count; i++) {
      if (hit[i]) {
        this.detached[i] = 1;
        this.invMass[i] = 1 / Math.max(this.mass[i]!, 0.05);
      }
    }
    this.health = area / this.totalArea;
    this.version++;
    return before - this.health;
  }

  /** Index buffer of the surviving triangles. */
  aliveIndices(): Uint32Array {
    let n = 0;
    for (let t = 0; t < this.triAlive.length; t++) if (this.triAlive[t]) n++;
    const out = new Uint32Array(n * 3);
    let k = 0;
    for (let t = 0; t < this.triAlive.length; t++) {
      if (!this.triAlive[t]) continue;
      out[k++] = this.indices[t * 3]!;
      out[k++] = this.indices[t * 3 + 1]!;
      out[k++] = this.indices[t * 3 + 2]!;
    }
    return out;
  }

  computeNormals(): void {
    const nrm = this.normal;
    const pos = this.pos;
    nrm.fill(0);
    const idx = this.indices;
    const alive = this.triAlive;
    for (let t = 0, tri = 0; t < idx.length; t += 3, tri++) {
      if (!alive[tri]) continue;
      const a = idx[t]! * 3, b = idx[t + 1]! * 3, c = idx[t + 2]! * 3;
      const e1x = pos[b]! - pos[a]!, e1y = pos[b + 1]! - pos[a + 1]!, e1z = pos[b + 2]! - pos[a + 2]!;
      const e2x = pos[c]! - pos[a]!, e2y = pos[c + 1]! - pos[a + 1]!, e2z = pos[c + 2]! - pos[a + 2]!;
      const nx = e1y * e2z - e1z * e2y;
      const ny = e1z * e2x - e1x * e2z;
      const nz = e1x * e2y - e1y * e2x;
      nrm[a]! += nx; nrm[a + 1]! += ny; nrm[a + 2]! += nz;
      nrm[b]! += nx; nrm[b + 1]! += ny; nrm[b + 2]! += nz;
      nrm[c]! += nx; nrm[c + 1]! += ny; nrm[c + 2]! += nz;
    }
    for (let i = 0; i < this.count; i++) {
      const o = i * 3;
      const l = Math.sqrt(nrm[o]! * nrm[o]! + nrm[o + 1]! * nrm[o + 1]! + nrm[o + 2]! * nrm[o + 2]!) || 1;
      nrm[o]! /= l;
      nrm[o + 1]! /= l;
      nrm[o + 2]! /= l;
    }
  }
}

function triArea(p: Float32Array, a: number, b: number, c: number): number {
  const ax = p[a * 3]!, ay = p[a * 3 + 1]!, az = p[a * 3 + 2]!;
  const e1x = p[b * 3]! - ax, e1y = p[b * 3 + 1]! - ay, e1z = p[b * 3 + 2]! - az;
  const e2x = p[c * 3]! - ax, e2y = p[c * 3 + 1]! - ay, e2z = p[c * 3 + 2]! - az;
  return 0.5 * Math.hypot(e1y * e2z - e1z * e2y, e1z * e2x - e1x * e2z, e1x * e2y - e1y * e2x);
}
